import React, { useState, useEffect } from 'react';
import { LIGHT, DARK } from './theme';
import { MEMBERS, PENDING_PAYMENTS, RECEIPTS_INITIAL } from './data';
import { AppShell, Sidebar, BottomNav, DesktopTopBar, useBreakpoint } from './components/Layout';
import LoginScreen from './screens/LoginScreen';
import DashboardScreen from './screens/DashboardScreen';
import PaymentScreen from './screens/PaymentScreen';
import { MyReceipts, MySavings, AppSettings } from './screens/MemberScreens';
import { AdminDashboard, AdminApprovals, AdminMembers, AdminCompliance } from './screens/AdminScreens';

const MEMBER_TABS = [
  { key: 'home', icon: '🏠', label: 'Home' },
  { key: 'pay', icon: '💳', label: 'Pay' },
  { key: 'savings', icon: '📊', label: 'Savings' },
  { key: 'receipts', icon: '🧾', label: 'Receipts' },
  { key: 'settings', icon: '⚙️', label: 'Settings' },
];

const ADMIN_TABS = (pendingCount) => [
  { key: 'home', icon: '🏠', label: 'Home' },
  { key: 'approvals', icon: '✅', label: 'Approve', badge: pendingCount > 0 ? pendingCount : null },
  { key: 'members', icon: '👥', label: 'Members' },
  { key: 'compliance', icon: '📊', label: 'Reports' },
  { key: 'settings', icon: '⚙️', label: 'Settings' },
];

export default function App() {
  const [isDark, setIsDark] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [memberTab, setMemberTab] = useState('home');
  const [adminTab, setAdminTab] = useState('home');
  const [members, setMembers] = useState(MEMBERS);
  const [pending, setPending] = useState(PENDING_PAYMENTS);
  const [receipts, setReceipts] = useState(RECEIPTS_INITIAL);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const bp = useBreakpoint();

  const t = isDark ? DARK : LIGHT;

  // Detect system theme on first load
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mq.matches);
    mq.addEventListener('change', e => setIsDark(e.matches));
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = () => {
    if (installPrompt) { installPrompt.prompt(); setShowInstallBanner(false); }
  };

  const handleLogin = (admin) => {
    setIsAdmin(admin);
    setLoggedIn(true);
    setMemberTab('home');
    setAdminTab('home');
  };

  const handleApprove = (payId) => {
    const pay = pending.find(p => p.id === payId);
    if (!pay) return;
    setPending(prev => prev.filter(p => p.id !== payId));
    // Auto-generate receipt
    const newReceipt = {
      id: `SBG-2025-P02-${String(receipts.length + 42).padStart(4, '0')}`,
      months: pay.months,
      amount: pay.amount,
      date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      archived: false,
      isNew: true,
    };
    setReceipts(prev => [newReceipt, ...prev]);
    setMembers(prev => prev.map(m => m.id === pay.memberId ? { ...m, paid: true, amount: pay.amount } : m));
  };

  const handleReject = (payId) => setPending(prev => prev.filter(p => p.id !== payId));

  const handleUpdateNotes = (memberId, newNotes) => {
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, notes: newNotes } : m));
  };

  const handlePaySubmit = () => {
    const newReceipt = { id: 'SBG-2025-P02-DRAFT', months: 'Jun + Jul 2025', amount: 16000, date: 'Pending', archived: false, isNew: false };
    setReceipts(prev => [newReceipt, ...prev]);
    setMemberTab('home');
  };

  // Determine active tab and screen title
  const tab = isAdmin ? adminTab : memberTab;
  const tabs = isAdmin ? ADMIN_TABS(pending.length) : MEMBER_TABS;
  const setTab = isAdmin ? setAdminTab : setMemberTab;

  const titles = {
    home: isAdmin ? 'Admin Dashboard' : 'Dashboard',
    pay: 'Submit Payment',
    savings: 'My Savings',
    receipts: 'My Receipts',
    approvals: 'Payment Approvals',
    members: 'Member Management',
    compliance: 'Reports & Export',
    settings: 'Settings',
  };

  const renderContent = () => {
    if (!isAdmin) {
      if (tab === 'home') return <DashboardScreen t={t} receipts={receipts} onNav={setMemberTab} />;
      if (tab === 'pay') return <PaymentScreen t={t} onSubmit={handlePaySubmit} />;
      if (tab === 'savings') return <MySavings t={t} />;
      if (tab === 'receipts') return <MyReceipts t={t} receipts={receipts} />;
      if (tab === 'settings') return <AppSettings t={t} isDark={isDark} onToggleDark={() => setIsDark(d => !d)} />;
    } else {
      if (tab === 'home') return <AdminDashboard t={t} pending={pending} members={members} onNav={setAdminTab} />;
      if (tab === 'approvals') return <AdminApprovals t={t} pending={pending} onApprove={handleApprove} onReject={handleReject} />;
      if (tab === 'members') return <AdminMembers t={t} members={members} onUpdateNotes={handleUpdateNotes} />;
      if (tab === 'compliance') return <AdminCompliance t={t} />;
      if (tab === 'settings') return <AppSettings t={t} isDark={isDark} onToggleDark={() => setIsDark(d => !d)} />;
    }
  };

  if (!loggedIn) return (
    <div style={{ background: t.bg, minHeight: '100vh', fontFamily: 'Nunito, sans-serif' }}>
      {showInstallBanner && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: '#1B5E20', padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 16 }}>📱</span>
          <div style={{ flex: 1, color: '#fff', fontSize: 13, fontWeight: 700 }}>Install SB Group app</div>
          <div onClick={handleInstall} style={{ background: '#C8A400', borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 800, color: '#1B5E20', cursor: 'pointer' }}>Install</div>
          <div onClick={() => setShowInstallBanner(false)} style={{ color: '#A5D6A7', cursor: 'pointer', fontSize: 18 }}>✕</div>
        </div>
      )}
      <LoginScreen t={t} onLogin={handleLogin} />
    </div>
  );

  // Desktop layout
  if (bp === 'desktop') {
    return (
      <div style={{ display: 'flex', height: '100vh', background: t.isDark ? '#020305' : '#E8F0E8', fontFamily: 'Nunito, sans-serif' }}>
        {/* Sidebar */}
        <div style={{ width: 280, flexShrink: 0, background: t.surface, borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column' }}>
          <Sidebar t={t} tabs={tabs} active={tab} onTab={setTab} isAdmin={isAdmin} onSwitchMode={() => { setIsAdmin(a => !a); setMemberTab('home'); setAdminTab('home'); }} />
        </div>
        {/* Main */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Desktop top bar */}
          <div style={{ background: t.isDark ? '#040f08' : t.primary2, padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>{titles[tab]}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div onClick={() => setIsDark(d => !d)} style={{ background: 'rgba(255,255,255,.15)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, color: '#fff', fontWeight: 700 }}>{isDark ? '☀️ Light' : '🌙 Dark'}</div>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, cursor: 'pointer' }}>🔔</div>
            </div>
          </div>
          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 0 40px' }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tablet layout
  if (bp === 'tablet') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: t.isDark ? '#020305' : '#C8DCC8', fontFamily: 'Nunito, sans-serif', padding: 20 }}>
        {showInstallBanner && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: '#1B5E20', padding: '12px 20px', display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 16 }}>📱</span>
            <div style={{ flex: 1, color: '#fff', fontSize: 14, fontWeight: 700 }}>Install SB Group on your device</div>
            <div onClick={handleInstall} style={{ background: '#C8A400', borderRadius: 8, padding: '6px 16px', fontSize: 13, fontWeight: 800, color: '#1B5E20', cursor: 'pointer' }}>Install</div>
            <div onClick={() => setShowInstallBanner(false)} style={{ color: '#A5D6A7', cursor: 'pointer', fontSize: 20 }}>✕</div>
          </div>
        )}
        <div style={{ width: 560, maxWidth: '100%', height: '90vh', background: t.bg, borderRadius: 24, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: t.isDark ? '0 0 60px rgba(129,199,132,.1), 0 20px 60px rgba(0,0,0,.6)' : '0 10px 50px rgba(0,0,0,.15)' }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>{renderContent()}</div>
          <BottomNav t={t} tabs={tabs} active={tab} onTab={setTab} />
        </div>
        <div style={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', gap: 8 }}>
          <div onClick={() => { setIsAdmin(a => !a); setMemberTab('home'); setAdminTab('home'); }} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: t.primary, boxShadow: '0 2px 10px rgba(0,0,0,.1)' }}>Switch to {isAdmin ? 'Member' : 'Admin'}</div>
          <div onClick={() => setIsDark(d => !d)} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: t.primary, boxShadow: '0 2px 10px rgba(0,0,0,.1)' }}>{isDark ? '☀️ Light' : '🌙 Dark'}</div>
        </div>
      </div>
    );
  }

  // Mobile layout
  return (
    <div style={{ width: '100vw', height: '100vh', background: t.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'Nunito, sans-serif' }}>
      {showInstallBanner && (
        <div style={{ background: '#1B5E20', padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 14 }}>📱</span>
          <div style={{ flex: 1, color: '#fff', fontSize: 12, fontWeight: 700 }}>Add SB Group to home screen</div>
          <div onClick={handleInstall} style={{ background: '#C8A400', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 800, color: '#1B5E20', cursor: 'pointer' }}>Install</div>
          <div onClick={() => setShowInstallBanner(false)} style={{ color: '#A5D6A7', cursor: 'pointer', fontSize: 16 }}>✕</div>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto' }}>{renderContent()}</div>
      <BottomNav t={t} tabs={tabs} active={tab} onTab={setTab} />
    </div>
  );
}
